import React, { useEffect, lazy, Suspense } from "react";
import { CircularProgress, Box } from "@mui/material";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";

import BarraNavegacion from "Componentes/BarraNavegacion";
import DashBoard from "DashBoard/DashBoard";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Login from "Login/Login";
import { peticionGet } from "Componentes/ComponenteAxios";

// Definición de rutas protegidas con lazy load
const rutasProtegidas = [
  {
    base: "Trabajadores",
    listado: lazy(() => import("TrabajadoresListado/TrabajadorListado")),
    // nuevo: lazy(() => import("Buques/BuqueNuevo")),
    // edicion: lazy(() => import("Buques/BuqueEdicion")),
  },
  {
    base: "Turnos",
    listado: lazy(() => import("TurnosListado/TurnoListado")),
    // nuevo: lazy(() => import("Buques/BuqueNuevo")),
    // edicion: lazy(() => import("Buques/BuqueEdicion")),
  },
  {
    base: "Pausas",
    listado: lazy(() => import("PausasListado/PausaListado")),
    // nuevo: lazy(() => import("Buques/BuqueNuevo")),
    // edicion: lazy(() => import("Buques/BuqueEdicion")),
  },
  {
    base: "Informes",
    listado: lazy(() => import("Informes/TabsEmpresa")),
  },
  {
    base: "Planificador",
    listado: lazy(() => import("Planificador/PlanificadorSemanal")),
  },
  {
    base: "PlanificadorImpresion",
    listado: lazy(() => import("PlanificadorImpresion/PlanificadorImpresion")),
  },
  {
    base: "InformesSemanales",
    listado: lazy(() => import("InformesSemanales/InformesSemanales")),
  },
  {
    base: "AsignarPlantillas",
    listado: lazy(() =>
      import("AsignarPlantillaTrabajador/AsignarPlantillaTrabajadorPage")
    ),
  },
  {
    base: "agrupacionesTurnos",
    listado: lazy(() => import("AgrupacionesTurnos/AgrupacionesTurnosPage")),
  },
  {
    base: "tramosDia",
    listado: lazy(() => import("TramosDia/TramosDiaPage")),
  },
  {
    base: "necesidadesPersonal",
    listado: lazy(() => import("NecesidadesPersonal/NecesidadesPersonalPage")),
  },
  {
    base: "plantillasAnuales",
    listado: lazy(() => import("PlantillasAnuales/PlantillasAnualesPage")),
    edicion: lazy(() => import("PlantillasAnuales/EditarPlantillaPage")),
    edicionMatriz: lazy(() =>
      import("PlantillasAnuales/EditarPlantillaMatrizPage")
    ),
    edicionPatron: lazy(() =>
      import("PlantillasAnuales/EditorPatrones/EditarPlantillaPatronesPage")
    ),
  },

  {
    base: "Patrones",
    listado: lazy(() => import("Patrones/GestionPatronesPage")),
  },
  {
    base: "guia",
    listado: lazy(() => import("GuiaInicio/GuiaInicioPage")),
  },

  {
    base: "DepartamentosSedes",
    listado: lazy(() => import("DepartamentosSedes/DepartamentosSedesListado")),
    nuevo: lazy(() => import("DepartamentosSedes/DepartamentosSedesNuevo")),
    edicion: lazy(() => import("DepartamentosSedes/DepartamentosSedesEdicion")),
    wrapper: lazy(() => import("DepartamentosSedes/DepartamentosSedesWrapper")),
    wrapperProps: {
      tabs: [
        {
          label: "Listado",
          id: "listado",
          componente: lazy(() =>
            import("DepartamentosSedes/DepartamentosSedesListado")
          ),
        },
        {
          label: "Nuevo",
          id: "nuevo",
          componente: lazy(() =>
            import("DepartamentosSedes/DepartamentosSedesNuevo")
          ),
        },
        {
          label: "Jerarquía",
          id: "jerarquia",
          componente: lazy(() =>
            import("DepartamentosSedes/DepartamentosSedesJerarquia")
          ),
        },
      ],
      defaultTab: "listado",
    },
  },
  {
    base: "PuestosTrabajo",
    listado: lazy(() => import("PuestosTrabajo/PuestosTrabajoListado")),
    nuevo: lazy(() => import("PuestosTrabajo/PuestosTrabajoNuevo")),
    edicion: lazy(() => import("PuestosTrabajo/PuestosTrabajoEdicion")),
  },
  {
    base: "AsignacionesPuestos",
    listado: lazy(() =>
      import("AsignacionesPuestos/AsignacionesPuestosListado")
    ),
    // nuevo: lazy(() => import("AsignacionesPuestos/AsignacionesPuestosNuevo")),
    // edicion: lazy(() => import("AsignacionesPuestos/AsignacionesPuestosEdicion")),
  },
];

// Componente de ruta protegida

const ProtectedRoute = ({ children }) => {
  const token = localStorage.getItem(process.env.REACT_APP_PREFIJO + "token");

  const handleLogout = () => {
    localStorage.removeItem(process.env.REACT_APP_PREFIJO + "token");
    window.location.href = "/login";
  };

  if (!token) {
    return <Navigate to="/login" replace />;
  }

  return (
    <>
      <BarraNavegacion onLogout={handleLogout} />
      <div style={{ marginTop: "20px" }}>{children}</div>
    </>
  );
};

const AppRoutes = () => {
  const theme = createTheme({
    palette: {
      primary: {
        // main: "#C0DB1A",
        main: "#71c160",
      },
      secondary: {
        // main: "#B3B8BB",
        main: "#2F4F4F",
      },
      error: {
        main: "#f44336", // Rojo
      },
      warning: {
        main: "#ffa726", // Naranja
      },
      info: {
        main: "#2196f3", // Azul claro (por defecto)
        light: "#6ec6ff",
        dark: "#0069c0",
        contrastText: "#ffffff",
      },
      success: {
        main: "#4caf50", // Verde
      },
      background: {
        secondary: "rgba(188, 189, 192, 0.8)", // Definir en background para más flexibilidad
      },
    },
    components: {
      MuiGrid: {
        styleOverrides: {
          root: {
            paddingTop: "2rem",
            // Aplica paddingTop solo para xs
            "@media (max-width:600px)": {
              paddingTop: "1rem", // Aplica el paddingTop con !important solo en pantallas xs
              paddingLeft: 0,
              paddingRight: 0,
            },
          },
        },
      },
    },
  });
  useEffect(() => {
    document.title = process.env.REACT_APP_TITULO;
  }, []);
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline /> {/* Esto aplica estilos globales de MUI */}
      <Router>
        <Suspense
          fallback={
            <Box
              sx={{ display: "flex", justifyContent: "center", marginTop: 10 }}
            >
              <CircularProgress />
            </Box>
          }
        >
          <Routes>
            <Route path="/login" element={<Login />} />

            {/* Ruta principal protegida */}
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <DashBoard />
                </ProtectedRoute>
              }
            />
            {/* 
        <Route
          path="/PartesTrabajoDetalle/:id"
          element={
            <ProtectedRoute>
              <PartesTrabajoDetalle />
            </ProtectedRoute>
          }
        /> */}

            {/* Rutas protegidas generadas dinámicamente */}
            {rutasProtegidas.map(
              ({
                base,
                listado,
                nuevo,
                edicion,
                editarCabecera,
                wrapper,
                wrapperProps,
                edicionMatriz,
                edicionPatron,
              }) => (
                <React.Fragment key={base}>
                  <Route
                    path={`/${base}`}
                    element={
                      <ProtectedRoute>
                        {wrapper
                          ? React.createElement(wrapper, {
                              ...(wrapperProps || {}),
                              tabs: wrapperProps?.tabs?.map((tab) => ({
                                ...tab,
                                componente: React.createElement(tab.componente),
                              })),
                            })
                          : React.createElement(listado)}
                      </ProtectedRoute>
                    }
                  />
                  {/* <Route
                path={`/${base}/Nuevo`}
                element={
                  <ProtectedRoute>{React.createElement(nuevo)}</ProtectedRoute>
                }
              /> */}
                  {nuevo && (
                    <Route
                      path={`/${base}/Nuevo`}
                      element={
                        <ProtectedRoute>
                          {React.createElement(nuevo)}
                        </ProtectedRoute>
                      }
                    />
                  )}
                  {edicion && (
                    <Route
                      path={`/${base}/Editar/:id`}
                      element={
                        <ProtectedRoute>
                          {React.createElement(edicion)}
                        </ProtectedRoute>
                      }
                    />
                  )}
                  {edicionMatriz && (
                    <Route
                      path={`/${base}/EditarMatriz/:id`}
                      element={
                        <ProtectedRoute>
                          {React.createElement(edicionMatriz)}
                        </ProtectedRoute>
                      }
                    />
                  )}
                  {edicionPatron && (
                    <Route
                      path={`/${base}/EditarPatron/:id`}
                      element={
                        <ProtectedRoute>
                          {React.createElement(edicionPatron)}
                        </ProtectedRoute>
                      }
                    />
                  )}

                  {/* <Route
                path={`/${base}/Editar/:id`}
                element={
                  <ProtectedRoute>
                    {React.createElement(edicion)}
                  </ProtectedRoute>
                }
              /> */}
                  {editarCabecera && (
                    <Route
                      path={`/${base}/EditarCabecera/:id`}
                      element={
                        <ProtectedRoute>
                          {React.createElement(editarCabecera)}
                        </ProtectedRoute>
                      }
                    />
                  )}
                </React.Fragment>
              )
            )}
          </Routes>
        </Suspense>
      </Router>
    </ThemeProvider>
  );
};

export default AppRoutes;
